import React, { useState } from 'react';
import { SenderInfo } from '../types';
import useSEO from '../hooks/useSEO';
import { Button, Field, FlowPage, Icon } from '../components/bx';
import { flowSteps } from './ItemsPage';
import '../styles/bx.css';

interface SenderPageProps {
  data: SenderInfo;
  isGetMe?: boolean;
  isSubscribe?: boolean;
  onChange: (data: SenderInfo) => void;
  onContinue: () => void;
  onBack?: () => void;
}

interface Errors {
  email?: string;
  fullName?: string;
  phone?: string;
}

export const validateContact = (data: { email: string; fullName: string; phone: string }): Errors => {
  const errors: Errors = {};
  if (!data.fullName.trim()) errors.fullName = 'Enter a full name';
  if (!data.email.trim()) errors.email = 'Enter an email address';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) errors.email = 'Enter a valid email address';
  if (!data.phone.trim()) errors.phone = 'Enter a phone number';
  else if (data.phone.replace(/\D/g, '').length < 10) errors.phone = 'Enter a valid phone number';
  return errors;
};

export default function SenderPage({ data, isGetMe, onChange, onContinue, onBack }: SenderPageProps) {
  const [errors, setErrors] = useState<Errors>({});

  useSEO({ title: 'Your details - BOQBOX', description: 'Tell us who’s placing this order.' });

  const handleContinue = () => {
    const next = validateContact(data);
    setErrors(next);
    if (!Object.keys(next).length) onContinue();
  };
  const set = (key: keyof SenderInfo, value: string) => {
    onChange({ ...data, [key]: value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  return (
    <FlowPage
      eyebrow={isGetMe ? 'Get me' : 'Send a gift'}
      title={isGetMe ? 'Your details' : 'Who’s sending this gift?'}
      subtitle={isGetMe ? 'We’ll send your receipt here and call if we need directions.' : 'We’ll send your receipt here. We tell the recipient it’s from you.'}
      steps={flowSteps(isGetMe)}
      step={2}
      onBack={onBack}
      narrow
      bar={
        <>
          <div className="bx-bar-copy"><small>Next</small><strong>{isGetMe ? 'Delivery address' : 'Recipient details'}</strong></div>
          <Button onClick={handleContinue}>Continue <Icon name="arrow" /></Button>
        </>
      }
    >
      <form className="bx-card bx-card-pad" onSubmit={(e) => { e.preventDefault(); handleContinue(); }} noValidate>
        <div className="bx-form two">
          <Field label="Full name" error={errors.fullName} span>
            <input className="bx-input" autoComplete="name" placeholder="e.g. Chioma Okafor" value={data.fullName} onChange={(e) => set('fullName', e.target.value)} />
          </Field>
          <Field label="Email" error={errors.email}>
            <input className="bx-input" type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" value={data.email} onChange={(e) => set('email', e.target.value)} />
          </Field>
          <Field label="Phone number" error={errors.phone}>
            <input className="bx-input" type="tel" inputMode="tel" autoComplete="tel" placeholder="0803 123 4567" value={data.phone} onChange={(e) => set('phone', e.target.value)} />
          </Field>
        </div>
        <button type="submit" hidden />
      </form>
      <div className="bx-alert info" style={{ marginTop: 14 }}>
        <Icon name="lock" /> <span>We only use your details for this order. No spam, ever.</span>
      </div>
    </FlowPage>
  );
}
